import type { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cn from 'classnames'
import React from 'react'
import backdropStyles from '../overlays/backdrop.module.css'
import { ScrollArea } from '../scroll-area'
import styles from './Menu.module.css'
import { ContentPosition, ContentProps, ItemElementProps, RootProps } from './Menu.types'
import { MenuItem, MenuLinkItem } from './MenuItem'

const PositionMap: Record<ContentPosition, Pick<DropdownMenuContentProps, 'side' | 'align'>> = {
  'top': { side: 'top', align: 'center' },
  'bottom': { side: 'bottom', align: 'center' },
  'left': { side: 'left', align: 'center' },
  'right': { side: 'right', align: 'center' },
  'top-left': { side: 'top', align: 'end' },
  'top-right': { side: 'top', align: 'start' },
  'bottom-left': { side: 'bottom', align: 'end' },
  'bottom-right': { side: 'bottom', align: 'start' },
}

export function Root({
  children,
  onOpenChange,
  defaultOpen,
  open,
  showMobileBackdrop = false,
  modal = false,
  ...rest
}: RootProps) {
  const [hideBackdrop, setHideBackdrop] = React.useState(!defaultOpen)

  const handleOpenChange = (isOpen: boolean) => {
    setHideBackdrop(!isOpen)
    onOpenChange?.(isOpen)
  }

  const isBackdropHidden = typeof open === 'boolean' ? !open : hideBackdrop

  return (
    <DropdownMenu.Root onOpenChange={handleOpenChange} defaultOpen={defaultOpen} open={open} modal={modal} {...rest}>
      {showMobileBackdrop && (
        <DropdownMenu.Portal>
          <div
            className={cn(
              backdropStyles.backdrop,
              styles.mobileBackdrop,
              { [styles.backdropHidden]: isBackdropHidden },
            )}
          />
        </DropdownMenu.Portal>
      )}
      {children}
    </DropdownMenu.Root>
  )
}

function MenuContent({
  children,
  position = 'bottom',
  className,
  wrapperClassName,
  sideOffset,
  scrollable = true,
  ...rest
}: React.PropsWithChildren<ContentProps>) {
  const { side, align } = PositionMap[position]

  const scrollableContent = scrollable
    ? (
        <ScrollArea.Root>
          <ScrollArea.Viewport className={styles.scrollableContent}>
            {children}
          </ScrollArea.Viewport>
          <ScrollArea.Scrollbar orientation="vertical">
            <ScrollArea.Thumb />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      )
    : children

  const content = (
    <DropdownMenu.Content
      align={align}
      side={side}
      collisionPadding={8}
      className={cn(
        styles.contentWrapper,
        { [styles.contentWrapperWithScroll]: scrollableContent },
        className,
      )}
      sideOffset={sideOffset}
      {...rest}
    >
      {scrollableContent}
    </DropdownMenu.Content>
  )

  return (
    <DropdownMenu.Portal>
      {wrapperClassName ? <div className={wrapperClassName}>{content}</div> : content}
    </DropdownMenu.Portal>
  )
}

export const Content = MenuContent

export function Item({
  ref,
  children,
  glyph,
  asChild,
  onSelect,
  onPointerDown,
  className,
  ...rest
}: ItemElementProps<'button', 'div'>) {
  return (
    <DropdownMenu.Item
      ref={ref}
      asChild={asChild ?? true}
      onSelect={onSelect}
      onPointerDown={onPointerDown}
      className={cn(styles.dropdownMenuItem, className)}
    >
      <MenuItem glyph={glyph} {...rest}>
        {children}
      </MenuItem>
    </DropdownMenu.Item>
  )
}

export function LinkItem({
  children,
  glyph,
  className,
  ref,
  ...rest
}: ItemElementProps<'a', 'div'>) {
  return (
    <DropdownMenu.Item ref={ref} asChild className={cn(styles.dropdownMenuItem, className)}>
      <MenuLinkItem glyph={glyph} {...rest}>
        {children}
      </MenuLinkItem>
    </DropdownMenu.Item>
  )
}

export function Trigger({ children, ...rest }: React.PropsWithChildren) {
  return <DropdownMenu.Trigger {...rest} asChild>{children}</DropdownMenu.Trigger>
}
